import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import NotificationSystem from 'react-notification-system';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Validator from 'components/Validator/Validator';
import { format } from 'date-fns';
import { get, sumBy, filter } from 'lodash';
import CSVReader from 'react-csv-reader';
import { Row } from 'reactstrap';

import Header from 'components/Header';
import Heading from 'components/Heading';
import { Grid, Column } from 'components/Layout';
import FormGroup from 'components/Form/FormGroup';
import FormGroupLabel from 'components/FormGroupLabel';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Modal from 'components/Modal/AnimatedModal';
import EditableTable from 'components/EditableTable';
import { Button } from 'components/Button';
import SecurityLock from 'components/Icons/SecurityLock';
import User from 'components/Icons/User';
import Datetime from 'react-datetime';

import { parseUrlParams } from 'utils/parseUrlParams';
import { formatCurrency } from 'utils/formatCurrency';
import { getDecimalsCount } from 'utils/helpers';
import { numberMask, currencyMask, unmask } from 'utils/masks';
import editIcon from 'assets/icons/edit_icon.svg';

import { checkinAction } from 'actions/workflow';

import {
  getSettlements,
  getBankAccountDetails,
  confrimCDA,
  updateCounterOfferStatus,
} from 'actions/settlement';

import {
  getApplications,
  getTestValuesData,
} from 'actions/application';

import comodo from 'assets/images/comodo.png';
import schema from './schema';
import style from './style.scss';
import arrowup from 'assets/icons/arrow-up.svg';
import moment from 'moment';

const dateArr = [];
for (let i = 1; i <= 31; i++) {
  dateArr.push({
    title: i,
    value: i,
  });
}

const DscComponent = (props) => {
  const { validator: { values, errors, onChangeHandler } } = props;
  const dispatch = useDispatch();
  const columns = [
    { name: 'Original Creditor' },
    { name: 'Current Creditor' },
    { name: 'Settlement ID' },
    { name: 'Account # (last 4)' },
    { name: 'Original Enrolled Account Balance' },
    { name: 'Amount Due to DSC' },
    { name: 'Custodian Fee' },
    { name: 'Pay-off Amount to Creditor' },
    { name: 'Estimated' },
    { name: 'Actual' },
    { name: 'Date of Settlement' },
  ];
  const [rows, setRows] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCDAProcessing, setIsCDAProcessing] = useState(false);
  const [isBankAccountExisting, setIsBankAccountExisting] = useState(false);
  const [isDebtValidation, setIsDebtValidation] = useState(false);
  const [clientId, setClientId] = useState('');
  const [cdaBalance, setCdaBalance] = useState(null);
  const [monthlySavingsAmt, setMonthlySavingsAmt] = useState(null);
  const [isEditableCDA, setIsEditableCDA] = useState(true);
  const [confirmedCDA, setConfirmedCDA] = useState(false);
  const [isRegradeModalShown, setIsRegradeModalShown] = useState(false);
  const [acceptingCounterOffer, setAcceptingCounterOffer] = useState(false);
  const [acceptingStatus, setAcceptingStatus] = useState(null);
  const [isPendingModalShown, setIsPendingModalShown] = useState(false);
  const [amountShort, setAmountShort] = useState(null);
  const [percentageShort, setPercentageShort] = useState(null);
  const [initialBankData, setInitialBankData] = useState(null);
  const [selectedTestSubject, setSelectedTestSubject] = useState(null);
  const [dateError, setDateError] = useState(false);
  const [applicationDetail, setApplicationDetail] = useState(null);
  const [testSubjectsJson, setTestSubjectsJson] = useState(null);
  const notification = useRef(null);
  const isPreQualified = useMemo(() => get(applicationDetail, 'statusCode') === '5029', [applicationDetail]);
  const isEditingEnabled = useMemo(() => !!((!isSubmitted && rows.length) || (!isSubmitted && isPreQualified)), [isSubmitted, isPreQualified, rows]);

  const loadHandleSetValues = () => {
    dispatch(getTestValuesData({
      url: '/lookup/set-values-fsl-merchant-portal',
      success: (res) => {
        const testList = res.applications.attributes.fundingForm;
        setTestSubjectsJson(testList);
      },
      fail: (err) => {
        console.log('Error', err);
      },
    }));
  };

  useEffect(() => {
    loadHandleSetValues();
  }, []);

  const getAccountHolderName = () => {
    const params = parseUrlParams(window.location.search);
    const { validator: { setValues } } = props;
    let setTestSubjectData = {};
    dispatch(getApplications({
      url: `/applications/?applicationId=${params.applicationId}`,
      success: (response) => {
        setClientId(get(response, ['data', 0, 'channel', 'attributes', 'clientId']));
        const accountHolderName = `${get(response, ['data', 0, 'applicant', 'firstName'])} ${get(response, ['data', 0, 'applicant', 'lastName'])}`;
        const testSubjects = testSubjectsJson && testSubjectsJson.find(i => i.title === accountHolderName);
        setSelectedTestSubject(testSubjects);

        if (testSubjects && testSubjects.value) {
          setTestSubjectData = {
            bankName: get(testSubjects.value, 'bankName') || '',
            routingNumber: get(testSubjects.value, 'bankABARoutingNumber') || '',
            accountNumber: get(testSubjects.value, 'bankAccountNumber') || '',
            dateOfEnrollment: moment().format('MM/DD/YYYY'),
            monthlyDraftDate: get(testSubjects.value, 'monthlyDraftDate') || '',
            totalNumberOfSuccessfulPayments: get(testSubjects.value, 'totalNoOfSuccessfulPayments') || '',
            settlementDate: moment().add(1, 'days').format('MM/DD/YYYY'), // today+1
          };
          if (testSubjects.value.CDABalance) {
            setCdaBalance(testSubjects.value.CDABalance);
          }
        }

        const initialBankDataTemp = {
          accountHolderName,
          ...setTestSubjectData,
        };
        setValues({
          ...values,
          ...initialBankDataTemp,
        });
      },
      fail: () => {
      },
    }));
  };

  const getCDABalance = () => {
    const params = parseUrlParams(window.location.search);
    dispatch(getApplications({
      url: `/applications/?applicationId=${params.applicationId}`,
      success: (response) => {
        setClientId(get(response, ['data', 0, 'channel', 'attributes', 'clientId']));
        setCdaBalance(formatCurrency(get(response, 'data.0.channel.attributes.customerDedicatedAccountBalance'), 2));
      },
      fail: () => {
      },
    }));
  };

  const getApplicationDetail = () => {
    const params = parseUrlParams(window.location.search);
    dispatch(getApplications({
      url: `/applications-view/search?sortBy=applicationDate&sortOrder=desc&channelCode=fsl&ApplicationId=${params.applicationId}`,
      success: (response) => {
        if (get(response, 'data[0].statusCode') !== '5029') {
          notification.current
          && notification.current.addNotification({
            message: 'Loan must be in \'pre-qualifieid\' status in order to complete this funding form',
            level: 'error',
            position: 'tc',
          });
        }
        setApplicationDetail(get(response, 'data[0]'));
        setIsPendingModalShown(get(response, 'data[0].statusCode') === '5004');
      },
      fail: () => {
      },
    }));
  };

  const getAllSettlements = () => {
    const params = parseUrlParams(window.location.search);
    setIsLoading(true);
    dispatch(getSettlements({
      url: `/fsl-settlements/${params.applicationId}`,
      success: (response) => {
        setRows(response.settlements.map(item => ({
          id: item.id || '',
          creditorName: item.creditorName,
          currentCreditor: item.currentCreditor || '',
          settlementId: item.settlementId || '',
          creditAccountNumber: item.creditAccountNumber,
          accountBalance: item.accountBalance,
          amount: item.current.amount,
          isActual: item.current.isActual,
          settlementDate: item.current.settlementDate,
          dscFee: item.dscFee,
          custodianFee: item.custodianFee,
        })));
        setMonthlySavingsAmt(formatCurrency(get(response, 'monthlySavingsAmount'), 2));
        setIsSubmitted(response.isSubmitted);
        setIsLoading(false);
        setIsRegradeModalShown(get(response, 'counterOfferStatus') === 'ActionRequired');
        setAmountShort(get(response, 'amountShort'));
        setPercentageShort(get(response, 'percentageShort'));
        onChangeHandler('settlementDate', response.settlementExpirationDate ? format(new Date(response.settlementExpirationDate), 'MM/dd/yyyy') : '');
      },
      fail: () => {
        setIsSubmitted(false);
        setIsLoading(false);
      },
    }));
  };

  const fetchBankAccountDetails = () => {
    const params = parseUrlParams(window.location.search);
    const { validator: { setValues } } = props;
    setIsLoading(true);
    dispatch(getBankAccountDetails({
      url: `/bank-accounts/Application/${params.applicationId}`,
      success: (response) => {
        setIsLoading(false);

        const bankDetails = filter(response, item => `${item.class}`.toLowerCase() === 'cda');
        const bankDetail = bankDetails.length === 1 ? bankDetails[0] : bankDetails.find(item => item.isPrimary);
        if (bankDetail) {
          const initialBankDataTemp = {
            accountHolderName: bankDetail.accountHolderName,
            bankName: bankDetail.bankName,
            routingNumber: bankDetail.routingNumber,
            accountNumber: bankDetail.accountNumber,
          };
          setIsBankAccountExisting(true);
          setInitialBankData(initialBankDataTemp);
          setValues({
            ...values,
            ...initialBankDataTemp,
          });
        } else {
          getAccountHolderName();
        }
      },
      fail: () => {
        getAccountHolderName();
        setIsLoading(false);
      },
    }));
  };

  const getCurrentDate = () => format(new Date(), 'M/dd/yyyy');

  const toogleCDAEdit = () => {
    setIsEditableCDA(!isEditableCDA);
  };

  const toogleRegradeModal = () => {
    setIsRegradeModalShown(!isRegradeModalShown);
  };

  const handleConfirmCDA = () => {
    const params = parseUrlParams(window.location.search);

    setIsCDAProcessing(true);
    dispatch(confrimCDA({
      url: `/fsl-settlements/${params.applicationId}/cda/confirm`,
      data: {
        customerDedicatedAccountBalance: unmask(cdaBalance || ''),
        monthlySavingsAmount: unmask(monthlySavingsAmt || ''),
      },
      success: (res) => { // eslint-disable-line
        setIsCDAProcessing(false);
        setIsEditableCDA(false);
        setConfirmedCDA(true);
        notification.current
        && notification.current.addNotification({
          message: 'Successfully Updated',
          level: 'success',
          position: 'tc',
        });
      },
      fail: (error) => { // eslint-disable-line
        setIsCDAProcessing(false);
        notification.current
        && notification.current.addNotification({
          message: 'We encountered an error.',
          level: 'error',
          position: 'tc',
        });
      },
    }));
  };

  const handleSaveRow = (row) => {
    const newRows = [...rows, row];
    setRows(newRows);
    setIsDebtValidation(false);
  };

  const handleCSVLoaded = (loaded) => {
    // validation of csv file.
    let valid = true;
    let errorType = 1;
    if (loaded.length < 2) { // at least it should have 1 data row.
      valid = false;
    }
    const data = [];
    const errorLineNumber = [];
    for (let i = 1; i < loaded.length; i++) {
      if (loaded[i].length !== 9) {
        if (!(i === loaded.length - 1 && loaded[i].length === 1)) { // ignore last line
          valid = false;
          errorLineNumber.push(i + 1);
        }
      } else if (
        getDecimalsCount(unmask(loaded[i][4])) > 2 ||
        getDecimalsCount(unmask(loaded[i][5])) > 2 ||
        getDecimalsCount(unmask(loaded[i][6])) > 2
      ) {
        valid = false;
        errorType = 2;
      } else {
        data.push(loaded[i]);
      }
    }
    if (!valid) {
      notification.current
      && notification.current.addNotification({
        message: errorType === 1 ? `CSV upload failed. Error found at lines: ${errorLineNumber.join(',')}` : 'Unable to accept CSV. Please use acceptable currency format.',
        level: 'error',
        position: 'tc',
      });
    } else {
      const newRows = [];
      for (let i = 0; i < data.length; i++) {
        newRows.push({
          creditorName: data[i][0],
          currentCreditor: data[i][1],
          settlementId: data[i][2],
          creditAccountNumber: data[i][3],
          accountBalance: Number(unmask(data[i][4])),
          dscFee: Number(unmask(data[i][5])),
          amount: Number(unmask(data[i][6])),
          isActual: data[i][7] ? data[i][7].toLowerCase() === 'true' : false,
          settlementDate: data[i][8],
        });
      }
      setRows([...rows, ...newRows]);
      setIsDebtValidation(false);
    }
    const inputElement = document.querySelector('#uploadCSV');
    if (inputElement) {
      inputElement.value = '';
    }
  };

  const handleCSVError = () => {
    notification.current
    && notification.current.addNotification({
      message: 'CSV upload failed',
      level: 'error',
      position: 'tc',
    });
  };

  const handleUpdateRow = (index, row) => {
    const newRows = rows.slice();
    newRows[index] = row;
    setRows(newRows);
    setIsDebtValidation(false);
  };

  const handleDeleteRow = (index) => {
    const newRows = rows.slice();
    newRows.splice(index, 1);
    setRows(newRows);
  };

  const handleInputChange = (event) => {
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleCDAChange = (event) => {
    event.preventDefault();

    switch (event.target.name) {
      case 'cdaBalance':
        setCdaBalance(event.target.value);
        break;
      default:
        setMonthlySavingsAmt(event.target.value);
    }
  };

  const handleCDABlur = (event) => {
    event.preventDefault();

    switch (event.target.name) {
      case 'cdaBalance':
        setCdaBalance(formatCurrency(unmask(cdaBalance), 2));
        break;
      default:
        setMonthlySavingsAmt(formatCurrency(unmask(monthlySavingsAmt), 2));
        break;
    }
  };

  const handleDateChange = (name, date) => {
    if (date.isValid && date.isValid()) {
      onChangeHandler(name, date.format('MM/DD/YYYY'));
    } else if (name === 'settlementDate') {
      onChangeHandler(name, date);
    }
  };

  const handleSubmitButton = (e) => {
    e.preventDefault();
    const params = parseUrlParams(window.location.search);
    const { validator: { validate } } = props;

    if (!confirmedCDA) {
      notification.current
      && notification.current.addNotification({
        message: 'Please confirm CDA Balance and Monthly Savings Amount',
        level: 'error',
        position: 'tc',
      });
      return;
    }

    if (validate(schema).isValid && !dateError && rows.length) {
      const amtF = parseFloat((sumBy(rows, item => Number(item.dscFee)) + sumBy(rows, item => Number(item.amount)))
        - parseFloat(unmask(cdaBalance || '')));
      if (amtF > get(applicationDetail, 'maxApprovalAmount', 0)) {
        notification.current
        && notification.current.addNotification({
          message: `Amount Financed cannot exceed $${formatCurrency(get(applicationDetail, 'maxApprovalAmount', 0), 2)}`,
          level: 'error',
          position: 'tc',
        });
        return;
      }
      const formData = {
        ...values,
        accountType: 'Savings',
        entityId: params.applicationId,
        entityType: 'Application',
        isPrimary: true,
        merchantName: 'Emmitt Morar',
        class: 'CDA',
        signature: values.signature.trim(),
      };

      const settlements = [];
      rows.forEach((item) => {
        settlements.push({ ...item, isCertificateOfSettlement: false });
      });

      setIsLoading(true);
      setIsDebtValidation(false);

      dispatch(checkinAction({
        data: {
          applicationId: params.applicationId,
          applicantName: 'string',
          settlementStatus: 'Unknown',
          applicationDate: getCurrentDate(),
          settlements,
          signatureBy: {
            name: values.signature.trim(),
            date: getCurrentDate(),
          },
          bankAccount: {
            accountHolderName: values.accountHolderName,
            bankABARoutingNumber: values.routingNumber,
            bankAccountNumber: values.accountNumber,
            bankName: values.bankName,
          },
          generateDocument: true,
          settlementExpirationDate: values.settlementDate,
          dateOfEnrollment: values.dateOfEnrollment,
          successfulPayments: values.totalNumberOfSuccessfulPayments,
          monthlyDraftDate: values.monthlyDraftDate,
        },
        url: '/fsl-settlements?regrade=true',
        // eslint-disable-next-line
        success: (response) => {
          if (get(response, 'counterOfferStatus') === 'ActionRequired') {
            getApplicationDetail();
            setIsLoading(false);
            setIsRegradeModalShown(true);
            setAmountShort(get(response, 'amountShort'));
            setPercentageShort(get(response, 'percentageShort'));
            return;
          }
          setIsLoading(!isBankAccountExisting);
          setIsSubmitted(true);

          if (
            !isBankAccountExisting ||
            (isBankAccountExisting && (
              get(initialBankData, 'accountHolderName') !== values.accountHolderName ||
              get(initialBankData, 'bankName') !== values.bankName ||
              get(initialBankData, 'routingNumber') !== values.routingNumber ||
              get(initialBankData, 'accountNumber') !== values.accountNumber
            ))
          ) {
            dispatch(checkinAction({
              data: formData,
              url: '/bank-accounts',
              // eslint-disable-next-line
              success: (response) => {
                setIsLoading(false);
                setIsSubmitted(true);
                setIsBankAccountExisting(true);
              },
              fail: (error) => {
                setIsLoading(false);
                notification.current
                && notification.current.addNotification({
                  message: error.data.message,
                  level: 'error',
                  position: 'tc',
                });
              },
            }));
          }
        },
        fail: (error) => {
          setIsLoading(false);
          notification.current
          && notification.current.addNotification({
            message: error.data.message,
            level: 'error',
            position: 'tc',
          });
        },
      }));
    } else if (!rows.length) {
      setIsDebtValidation(true);
    }
  };

  const handleReturnActionItems = () => {
    setIsPendingModalShown(false);
    const params = parseUrlParams(window.location.search);
    props.history.push(`/dashboard/todo-list?applicationId=${params.applicationId}`);
  };

  const checkDate = () => {
    if ((new Date(values.settlementDate) < new Date('01/01/1950')) || (new Date(values.settlementDate) > new Date('12/31/2070'))) {
      setDateError(true);
    } else {
      setDateError(false);
    }
  };

  const handleAccept = (status) => {
    const params = parseUrlParams(window.location.search);
    setAcceptingCounterOffer(true);
    setAcceptingStatus(status);
    dispatch(updateCounterOfferStatus({
      url: `fsl-settlements/${params.applicationId}/counter-offer-status?isFundingForm=true`,
      data: `"${status}"`,
      success: () => {
        dispatch(getApplications({
          url: `/applications-view/search?sortBy=applicationDate&sortOrder=desc&channelCode=fsl&ApplicationId=${params.applicationId}`,
          success: (response) => {
            setAcceptingCounterOffer(false);
            notification.current
            && notification.current.addNotification({
              message: status === 'Accepted'
                ? 'Counter Offer Accepted'
                : 'Application Status changed to Declined',
              level: 'success',
              position: 'tc',
            });
            toogleRegradeModal();
            setApplicationDetail(get(response, 'data[0]'));
            setIsPendingModalShown(get(response, 'data[0].statusCode') === '5004');
          },
          fail: (error) => {
            setAcceptingCounterOffer(false);
            notification.current
            && notification.current.addNotification({
              message: get(error, 'message') || 'We encountered an error',
              level: 'error',
              position: 'tc',
            });
          },
        }));
      },
      fail: (error) => { // eslint-disable-line
        setAcceptingCounterOffer(false);
        notification.current
        && notification.current.addNotification({
          message: get(error, 'message') || 'We encountered an error',
          level: 'error',
          position: 'tc',
        });
      },
    }));
  };

  useEffect(() => {
    if (testSubjectsJson) {
      fetchBankAccountDetails();
      getAllSettlements();
      getApplicationDetail();
      getCDABalance();
    }
  }, [testSubjectsJson]);

  return (
    <Fragment>
      <Header />
      <NotificationSystem ref={notification} />
      <section className={`grid-container fluid borrowers-apply page-debts section ${style['page-debts']}`}>
        <div className="grid-container">
          <Grid className="grid-margin-x">
            <Column className="large-8 small-12 main-column">
              <Heading
                heading="Debts & Account Information"
                subHeading="The Loan Proceeds will be deposited into the Customer Dedicated Account indicated below."
              />
              <FormGroup>
                <FormGroupLabel value="Customer Dedicated Account Information" />
                <Grid className="grid-margin-x">
                  <Column className="small-6 medium-6">
                    <Input
                      label="Account Holder Name"
                      name="accountHolderName"
                      value={values.accountHolderName}
                      onChange={handleInputChange}
                      isRequired
                      hasError={!!errors.accountHolderName}
                      readOnly
                      isBadgeVisible={false}
                      errorMessage={errors.accountHolderName}
                    />
                  </Column>
                  <Column className="small-6 medium-6">
                    <Input
                      label="Bank Name"
                      name="bankName"
                      value={values.bankName}
                      onChange={handleInputChange}
                      isRequired={isEditingEnabled}
                      hasError={!!errors.bankName}
                      isDisabled={(isBankAccountExisting && !isPreQualified) || !isEditingEnabled}
                      isBadgeVisible={!isBankAccountExisting || isEditingEnabled}
                      errorMessage={errors.bankName}
                    />
                  </Column>
                  <Column className="small-12 medium-6">
                    <Input
                      label="Bank ABA Routing Number"
                      name="routingNumber"
                      value={`${values.routingNumber || ''}`}
                      onChange={handleInputChange}
                      isRequired={isEditingEnabled}
                      hasError={!!errors.routingNumber}
                      isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                      isDisabled={(isBankAccountExisting && !isPreQualified) || !isEditingEnabled}
                      isBadgeVisible={!isBankAccountExisting || isEditingEnabled}
                      errorMessage={errors.routingNumber}
                    />
                  </Column>
                  <Column className="small-12 medium-6">
                    <Input
                      label="Bank Account Number"
                      name="accountNumber"
                      value={`${values.accountNumber || ''}`}
                      onChange={handleInputChange}
                      isRequired={isEditingEnabled}
                      hasError={!!errors.accountNumber}
                      isDisabled={(isBankAccountExisting && !isPreQualified) || !isEditingEnabled}
                      isBadgeVisible={!isBankAccountExisting || isEditingEnabled}
                      isMasked={numberMask}
                      errorMessage={errors.accountNumber}
                    />
                  </Column>
                </Grid>
              </FormGroup>
              <FormGroup>
                <FormGroupLabel value="Statement of Enrolled Debts" />
                <Grid className="grid-margin-x">
                  <Column className="small-12 medium-6">
                    <Datetime
                      onChange={handleDateChange.bind(null, 'dateOfEnrollment')}
                      closeOnSelect
                      dateFormat="MM/DD/YYYY"
                      timeFormat={false}
                      className="dateTime-container"
                      inputProps={{
                        hasError: !!errors.dateOfEnrollment,
                        errorMessage: errors.dateOfEnrollment,
                      }}
                      renderInput={propsInput => (
                        <Input
                          {...propsInput}
                          label="Date of Enrollment"
                          name="dateOfEnrollment"
                          value={`${values.dateOfEnrollment || ''}`}
                          isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                          isRequired
                          hasError={!!errors.dateOfEnrollment}
                          errorMessage={errors.dateOfEnrollment}
                          isDisabled={false}
                          isBadgeVisible
                          onChange={({ target }) => onChangeHandler('dateOfEnrollment', target.value)}
                        />
                      )}
                    />
                  </Column>
                  <Column className="small-12 medium-6">
                    <Select
                      name="monthlyDraftDate"
                      data={dateArr}
                      value={`${values.monthlyDraftDate || ''}`}
                      onChange={handleInputChange}
                      label="Monthly Draft Date"
                      isRequired
                      hasError={!!errors.monthlyDraftDate}
                      errorMessage={errors.monthlyDraftDate}
                      labelClassName="w-100"
                    />
                  </Column>
                  <Column className="small-12 medium-6">
                    <Input
                      label="Total # of Successful Payments"
                      name="totalNumberOfSuccessfulPayments"
                      value={`${values.totalNumberOfSuccessfulPayments || ''}`}
                      onChange={handleInputChange}
                      hasError={!!errors.totalNumberOfSuccessfulPayments}
                      errorMessage={errors.totalNumberOfSuccessfulPayments}
                      isMasked={numberMask}
                      isRequired
                    />
                  </Column>
                  <Column className="small-12 medium-6">
                    <Datetime
                      onChange={handleDateChange.bind(null, 'settlementDate')}
                      closeOnSelect
                      dateFormat="MM/DD/YYYY"
                      isValidDate={current =>
                        current.isAfter('01/01/1950') && current.isBefore('12/31/2070')
                      }
                      timeFormat={false}
                      className="dateTime-container"
                      inputProps={{ readOnly: !isEditingEnabled, disabled: !isEditingEnabled }}
                      renderInput={propsInput => (
                        <Input
                          {...propsInput}
                          label="Settlement Expiration Date"
                          name="settlementDate"
                          value={values.settlementDate || ''}
                          isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                          isRequired={isEditingEnabled}
                          hasError={!!errors.settlementDate}
                          isDisabled={!isEditingEnabled}
                          isBadgeVisible={isEditingEnabled}
                          errorMessage={dateError ? 'Invalid date' : errors.settlementDate}
                          onBlur={checkDate}
                        />
                      )}
                    />
                  </Column>
                </Grid>
              </FormGroup>
            </Column>
            <Column className="large-4 sidebar-apply sticky-container" data-sticky-container="">
              <div className="sticky" data-sticky="" data-top-anchor="Sidebar" data-btm-anchor="Footer" data-margin-top="7.5" data-resize="mn7cem-sticky" data-mutate="mn7cem-sticky" data-e="4uqwkw-e">
                <div className="card">
                  <div className={style['card-header']}><h5>Client Information</h5></div>
                  <div className={cn('card-section', style['card-section'])}>
                    <Grid>
                      <Column>
                        <h6 className={`${style.heading6} text-right`}>Client Name</h6>
                        <p>{get(applicationDetail, 'applicantName')}</p>
                      </Column>
                      <Column>
                        <h6 className={style.heading6}>Max Approval Amount</h6>
                        <p>${formatCurrency(get(applicationDetail, 'maxApprovalAmount'), 2)}</p>
                      </Column>
                      <Row className={style['cda-area']}>
                        <h6 className={style.heading6}>CDA Balance</h6>
                        {isPreQualified && (
                          isEditableCDA
                            ? <p className={style['cda-close-btn']} onClick={toogleCDAEdit}>X</p>
                            : <img className={style['cda-edit-btn']} src={editIcon} onClick={toogleCDAEdit} alt="Edit" />
                        )}
                      </Row>
                      {
                        isEditableCDA && isPreQualified
                          ? (
                            <Input
                              name="cdaBalance"
                              value={`${cdaBalance || ''}`}
                              onChange={handleCDAChange}
                              onBlur={handleCDABlur}
                              isMasked={currencyMask}
                              isDisabled={!isEditableCDA}
                              isRequired
                            />
                          )
                          : (<p>${cdaBalance}</p>)
                      }
                      <Column>
                        <h6 className={style.heading6}>Monthly Savings Amount</h6>
                        {
                          isEditableCDA && isPreQualified
                            ? (
                              <Row className={style['confirm-cda-btn-wrapper']}>
                                <Input
                                  name="monthlySavingsAmt"
                                  labelClassName={style['monthly-saving-amt-field']}
                                  value={`${monthlySavingsAmt || ''}`}
                                  onChange={handleCDAChange}
                                  onBlur={handleCDABlur}
                                  isMasked={currencyMask}
                                  isDisabled={!isEditableCDA}
                                  isRequired
                                />
                                <Button
                                  className={`button small green ${style['confirm-cda-btn']}`}
                                  onClick={handleConfirmCDA}
                                  isLoading={isCDAProcessing}
                                >
                                  Confirm
                                </Button>
                              </Row>
                            )
                            : (<p>${monthlySavingsAmt}</p>)
                        }
                      </Column>
                      <Column>
                        <h6 className={style.heading6}>LendingUSA Loan ID</h6>
                        <p>{get(applicationDetail, 'applicationId')}</p>
                      </Column>
                      <Column>
                        <h6 className={style.heading6}>Client ID</h6>
                        <p>{clientId || '-'}</p>
                      </Column>
                      <Column>
                        <h6 className={style.heading6}>Merchant Name</h6>
                        <p>{get(applicationDetail, 'merchantName')}</p>
                      </Column>
                    </Grid>
                  </div>
                </div>
                <Grid className="security">
                  <Column className="small-9 ssl d-flex">
                    <SecurityLock />
                    <p className="p-small" style={{ marginLeft: '12px' }}><strong>128-bit SSL</strong> protection and strict encryption</p>
                  </Column>
                  <Column className="small-3 certificate">
                    <img src={comodo} alt="comodo" className="sectigo-image" />
                  </Column>
                </Grid>
              </div>
            </Column>
            <Column className="large-12">
              <FormGroup>
                <h4>Statement of Enrolled Debts {isDebtValidation && <span className={style.error}>Please enter statement of debts</span>}</h4>
                {isEditingEnabled &&
                  <Fragment>
                    <div className={style.csvLoader}>
                      <img src={arrowup} alt="arrowup" />
                      <span>Upload CSV File</span>
                      <Button
                        className="button green"
                        onClick={e => e.stopPropagation()}
                      >
                        Submit
                      </Button>
                      <CSVReader
                        cssClass="csv-reader-input"
                        label=""
                        onFileLoaded={handleCSVLoaded}
                        onError={handleCSVError}
                        inputId="uploadCSV"
                        inputStyle={{ color: 'red' }}
                      />
                    </div>
                  </Fragment>
                }
                <EditableTable
                  rows={rows}
                  columns={columns}
                  saveRow={handleSaveRow}
                  updateRow={handleUpdateRow}
                  deleteRow={handleDeleteRow}
                  isSubmitted={!isEditingEnabled || isSubmitted}
                  isLoading={isLoading}
                  testSubject={selectedTestSubject}
                />
              </FormGroup>
            </Column>
            {/* <Column className="large-12">
              <div className={style.overview}>
                <h5 className={style.heading5}>Estimated DSC Fee:</h5>
                <span className={style.infoSpan}>
                  {`${this.state.rows.length ? `${
                    lowAmount > 0 ? `$${formatCurrency(lowAmount, 2)}` : `-$${formatCurrency(Math.abs(lowAmount), 2)}`
                  }` : '-'} up to $${formatCurrency(highAmount, 2)}`}
                </span>
              </div>
            </Column> */}
            <Column className="large-12">
              <div className={style.overview}>
                <h5 className={style.heading5}>Amount Financed</h5>
                <span className={style.infoSpan}>${
                formatCurrency(
                  Math.max((sumBy(rows, item => Number(item.dscFee))
                    + sumBy(rows, item => Number(item.amount)))
                    - parseFloat(unmask(cdaBalance || '')), 0),
                  2
                )}
                </span>
              </div>
            </Column>
            {!isSubmitted &&
              <Column className={cn('large-8 large-offset-2', style.signatureWrapper)}>
                <FormGroup>
                  <h4>Your Signature</h4>
                  <Grid className="grid-margin-x">
                    <Column className="small-12 medium-9">
                      <Input
                        label="E-Signature (Type full name)"
                        name="signature"
                        value={values.signature}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!errors.signature}
                        errorMessage={errors.signature}
                        isDisabled={!isEditingEnabled}
                        readOnly={!isEditingEnabled}
                      />
                    </Column>
                    <Column className="small-12 medium-3">
                      <Input
                        label="Date"
                        name="date"
                        value={getCurrentDate()}
                        isDisabled
                        // hasError={!!errors['applicant.firstName']}
                      />
                    </Column>
                  </Grid>

                  <Button
                    className="button large arrow green w-100"
                    onClick={handleSubmitButton}
                    isLoading={isLoading}
                    isDisabled={!isEditingEnabled}
                  >
                    Submit
                  </Button>
                </FormGroup>
              </Column>
            }
          </Grid>
        </div>
      </section>
      <Modal
        className="sm-modal"
        isShown={isRegradeModalShown}
        isCloseButtonShown={false}
      >
        <Row>
          <p className="text-center">
            The Requested Amount cannot be approved. However, we can approve a lower amount. Please click below to ACCEPT this counter offer and make this adjustment to the Funding Form.
          </p>
        </Row>
        <div className={style.priceContainer}>
          <div className={style.priceItem}>
            <div>Requested Amount</div>
            <div>${formatCurrency(get(applicationDetail, 'requestedAmount', 0), 2)}</div>
          </div>
          <div className={style.maxApprovalAmount}>
            <div>${formatCurrency(get(applicationDetail, 'maxApprovalAmount', 0), 2)}</div>
            <div>Max Approval Amount</div>
          </div>
          <div className={style.priceItem}>
            <div>Amount Short</div>
            <div>${amountShort ? formatCurrency(amountShort, 2) : ' -'}</div>
          </div>
          <div className={style.priceItem}>
            <div>Percentage Short</div>
            <div>{percentageShort ? formatCurrency(percentageShort, 2) : ' -'}%</div>
          </div>
        </div>
        <Button
          className="button green arrow w-100"
          isLoading={acceptingCounterOffer && acceptingStatus === 'Accepted'}
          onClick={() => (handleAccept('Accepted'))}
          isDisabled={acceptingCounterOffer && acceptingStatus === 'CannotProceed'}
        >
          ACCEPT
        </Button>
        <Button
          className="button arrow w-100"
          isLoading={acceptingCounterOffer && acceptingStatus === 'CannotProceed'}
          onClick={() => (handleAccept('CannotProceed'))}
          isDisabled={acceptingCounterOffer && acceptingStatus === 'Accepted'}
        >
          Cannot Proceed
        </Button>
      </Modal>
      <Modal
        className="xs-modal border-radius-20"
        isShown={isPendingModalShown}
        isCloseButtonShown={false}
      >
        <Row>
          <User width={80} className={style['pending-avatar']} />
          <p className="text-center mt-1">
            This application is currently pending manual review
          </p>
        </Row>
        <Button
          className="button green w-100"
          onClick={handleReturnActionItems}
        >
          Return to Action Items
        </Button>
      </Modal>
    </Fragment>
  );
};

DscComponent.propTypes = {
  validator: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

DscComponent.defaultProps = {
};

export default compose(
  Validator(schema),
  withRouter
)(DscComponent);



// WEBPACK FOOTER //
// ./src/containers/pages/DscComponent/index.js